import { LOCKS, MESSAGES } from './copy';

/**
 * The server's error envelope, turned into something the tab can render.
 * `errorCodes` runs parallel to `errors` under the same keys, so the page picks
 * its wording by glossary code rather than by matching message text. Per-account
 * keys are dotted paths — "accounts.4711" — naming a row rather than a field.
 *
 * NOTE the codes are pluralized: `:account_user` emits
 * "decisiv:account_users:003". PD-4070's description quotes the singular and is
 * wrong about it; matching on that would silently never fire.
 */

export const ACCOUNT_PREFIX = 'accounts.';

export const SELF_MODIFICATION_CODE = 'decisiv:account_users:003';
export const LAST_ROLE_CODE = 'decisiv:account_users:002';

// The same constant the disabled role controls use as their tooltip, so a
// request that gets past them reads the sentence the admin was already shown
// rather than a second phrasing of it.
const CODE_MESSAGES = {
  [SELF_MODIFICATION_CODE]: LOCKS.selfModify,
  [LAST_ROLE_CODE]: LOCKS.lastRole,
};

const accountIdFrom = (key) => key.slice(ACCOUNT_PREFIX.length);

// A code the client has no wording for falls back to the server's own message,
// which is a whole sentence, rather than to a generic that says less.
const messagesFor = (messages, codes) =>
  (messages || []).map(
    (message, index) =>
      CODE_MESSAGES[(codes?.[index])] || message || MESSAGES.generic,
  );

/**
 * @returns {{base: string[], byAccount: Object<string, string[]>}}
 */
export const toAccessErrors = (errors = {}, errorCodes = {}) =>
  Object.keys(errors).reduce(
    (mapped, key) => {
      const messages = messagesFor(errors[key], errorCodes[key]);

      if (!key.startsWith(ACCOUNT_PREFIX)) {
        return { ...mapped, base: [...mapped.base, ...messages] };
      }

      return {
        ...mapped,
        byAccount: { ...mapped.byAccount, [accountIdFrom(key)]: messages },
      };
    },
    { base: [], byAccount: {} },
  );

export default toAccessErrors;
